<template>
  <div class="table-responsive  downline-rep-list-table" ref="tableContainer">
    <table class="table">
      <thead>
        <tr>
          <th>Status</th>
          <th>Name(Code) & Group(s)</th>
          <th>Upline</th>
          <th>Contact Info</th>
          <th>Direct Totals</th>
          <th>Downline Totals</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <!-- First load: show quick buttons inside the table -->
        <tr v-if="isInitialLoad">
          <td colspan="7" class="empty-state-cell">
            <div class="quick-date-btns-inline d-flex align-items-center justify-content-center gap-2 flex-wrap mb-3">
              <button
                type="button"
                class="quick-date-btn-inline"
                :class="{ active: activeQuickFilter === 'all' }"
                @click="$emit('quick-filter', 'all')"
                title="All reps"
              >
                <i class="fas fa-list me-1"></i>
                All Reps
              </button>
              <button
                type="button"
                class="quick-date-btn-inline"
                :class="{ active: activeQuickFilter === 'today' }"
                @click="$emit('quick-filter', 'today')"
                title="Reps signed up today"
              >
                <i class="fas fa-calendar-day me-1"></i>
                Reps Today
              </button>
              <button
                type="button"
                class="quick-date-btn-inline"
                :class="{ active: activeQuickFilter === 'week' }"
                @click="$emit('quick-filter', 'week')"
                title="Reps signed up this week"
              >
                <i class="fas fa-calendar-week me-1"></i>
                Reps This Week
              </button>
              <button
                type="button"
                class="quick-date-btn-inline"
                :class="{ active: activeQuickFilter === 'month' }"
                @click="$emit('quick-filter', 'month')"
                title="Reps signed up this month"
              >
                <i class="fas fa-calendar-alt me-1"></i>
                Reps This Month
              </button>
            </div>
            <div class="empty-state-msg">
              <i class="fas fa-search me-2"></i>
              Enter search criteria or use a quick search above to display results.
            </div>
          </td>
        </tr>

        <!-- After search: no results -->
        <tr v-else-if="!rows.records">
          <td colspan="7" align="center">No records found.</td>
        </tr>

        <!-- Data rows -->
        <tr
          v-else
          v-for="(row, index) in rows.records"
          :key="index"
          :class="`row-status-${row.agent_status.toLowerCase()}`"
        >
          <td>
            <span>
              Signup Date:
              <strong>{{ row.agent_signup_date }}</strong>
            </span>
            <span :class="`id-status-${row.agent_status.toLowerCase()}`">
              {{ row.agent_status }}
            </span>
            <span>
              Level:
              <b>{{ row.agent_level }}</b>
            </span>
            <div>
              Premier Level:
              <strong>
                {{
                  row.agent_level === null || row.agent_level === 0
                    ? "N/A"
                    : row.agent_level
                }} </strong
              ><br />
              Smart Level:
              <strong>
                {{
                  row.agent_level_2 === null || row.agent_level_2 === 0
                    ? "N/A"
                    : row.agent_level_2
                }}
              </strong>

              <template
                v-if="Array.isArray(row.contracts) && row.contracts.length >= 1"
              >
                <div
                  v-for="(signedContract, index) in row.contracts.slice(0, 2)"
                  :key="index"
                >
                  <template
                    v-if="
                      signedContract.data && Array.isArray(signedContract.data)
                    "
                  >
                    <div
                      v-for="(signedCont, ind) in signedContract.data.slice(
                        0,
                        2
                      )"
                      :key="ind"
                    >
                      {{ signedCont.contract_item_display_name }}
                      Level: <strong>{{ signedCont.contract_level }}</strong>
                    </div>
                  </template>
                </div>
                <a
                  class="signed-contract-details"
                  v-if="
                    row.contracts.some(
                      (contract) => contract.data && contract.data.length > 2
                    )
                  "
                  @click="openSignedContractModal(row.contracts)"
                >
                  Show More
                </a>
              </template>
            </div>
          </td>
         <td>
            <!-- Company Name (if exists) -->
           <div v-if="row.business && getBusinessName(row.business)" class="company-name">
              <strong>Business Name:</strong>
              <div>{{ getBusinessName(row.business) }}</div>
            </div>
            <!-- Rep Personal Name -->
            <div class="rep-name">
              <strong>Agent Name:</strong>
              {{ row.agent_fname }} {{ row.agent_lname }}
            </div>


            <!-- Agent Code -->
            <div v-if="row.agent_code" class="rep-code">
              <strong>({{ row.agent_code }})
              </strong>
            </div>

            <!-- Groups (if exists) -->
            <div v-if="row.groups && row.groups.length" class="rep-groups">
              <strong>Groups:</strong>
              <span v-for="(g, index) in row.groups" :key="index">
                {{ g }}<template v-if="index !== row.groups.length - 1">, </template>
              </span>
            </div>
          </td>

         
          <td v-if="row.upline_details">
            <span>
              {{ row.upline_details.upline_first_name }}
              {{ row.upline_details.upline_last_name }}
            </span>

            <span>{{ row.upline_details?.upline_agent_code }}</span>
            <span>
              <strong>Level:</strong>
              {{ row.upline_details?.upline_agent_level }}
            </span>
          </td>
          <td v-else>Upline agent not found</td>
          <td>
            <span>
              <strong>Location:</strong>
              {{ row.agent_city }},{{ row.agent_state }}
            </span>
            <span>
              <strong>Phone 1:</strong>
              {{ row.agent_phone1 }}
              <valid-icon
                v-if="row.agent_phone1"
                :is-valid="row.is_phone1_valid"
                className="valid-icon"
              />
            </span>
            <span v-if="row.agent_phone2">
              <strong>Phone 2:</strong>
              {{ row.agent_phone2 }}
              <valid-icon
                v-if="row.agent_phone2"
                :is-valid="row.is_phone2_valid"
                className="valid-icon"
              />
            </span>
            <span>
              <strong>Email / Username:</strong>
              <br />
              {{ row.agent_email }}
              <valid-icon
                :is-valid="row.is_email_valid"
                className="valid-icon"
              />
            </span>
            <span class="info-link" v-if="row.agent_profile_link">
              {{
                row.agent_profile_link.title
                  ? `${row.agent_profile_link.title}:`
                  : ""
              }}
              {{ row.agent_profile_link.link }}
            </span>
          </td>
          <td>
            <ul>
              <li>
                <strong>A:</strong>
                {{ row.agent_active_total }}
              </li>
              <li>
                <strong>W:</strong>
                {{ row.agent_withdraw_total }}
              </li>
              <li>
                <strong>T:</strong>
                {{ row.agent_termed_total }}
              </li>
              <li>
                <strong>Totals:</strong>
                {{
                  getStatTotal(
                    row.agent_active_total,
                    row.agent_withdraw_total,
                    row.agent_termed_total
                  )
                }}
              </li>
            </ul>
            <template
              v-if="
                getStatTotal(
                  row.agent_active_total,
                  row.agent_withdraw_total,
                  row.agent_termed_total
                ) > 0
              "
            >
              <router-link
                :to="{
                  name: 'MemberListByRep',
                  params: { repId: encodedId(row.agent_id), type: 'direct' },
                }"
                target="_blank"
                class="viewClient-btn"
              >
                View Clients
              </router-link>
            </template>
          </td>
          <td>
            <ul>
              <li>
                <strong>A:</strong>
                {{ row.agent_active_downline_policy }}
              </li>
              <li>
                <strong>W:</strong>
                {{ row.agent_withdrawn_downline_policy }}
              </li>
              <li>
                <strong>T:</strong>
                {{ row.agent_termed_downline_policy }}
              </li>
              <li>
                <strong>Totals:</strong>
                {{
                  getStatTotal(
                    row.agent_active_downline_policy,
                    row.agent_withdrawn_downline_policy,
                    row.agent_termed_downline_policy
                  )
                }}
              </li>
            </ul>
            <template
              v-if="
                getStatTotal(
                  row.agent_active_downline_policy,
                  row.agent_withdrawn_downline_policy,
                  row.agent_termed_downline_policy
                ) > 0
              "
            >
              <router-link
                :to="{
                  name: 'MemberListByRep',
                  params: { repId: encodedId(row.agent_id), type: 'downline' },
                }"
                target="_blank"
                class="viewClient-btn"
              >
                View Clients
              </router-link>
            </template>
          </td>
          <td v-if="row.agent_status.toLowerCase() !== 'deleted'">
            <div class="action-btns">
              <custom-route-button
                title="View/Edit Details"
                iconClass="fas fa-info"
                :routeLink="{
                  name: 'DownlineRepDetail',
                  params: { downlineRepId: encodedId(row.agent_id) },
                }"
              />
              <template v-if="row.actions.show_disable_agent == 1">
                <template v-if="row.actions.disable_agent == 1">
                  <custom-button
                    title="Disable Agent"
                    iconClass="fas fa-minus"
                    @click.native="enableDisableAgent(row.agent_id, 0)"
                  />
                </template>
              </template>
              <template
                v-if="
                  row.actions.show_elite_blue == 1 &&
                    row.actions.enable_elite_blue == 0
                "
              >
                <!-- <custom-button
                  title="Enable Elite Blue"
                  iconClass="fas fa-user-check"
                  @click.native="enableEliteBlue(row.agent_id)"
                /> -->
              </template>
              <!--
              <a href="#" v-b-tooltip.hover title="View Rep Analytics">
                <i class="fas fa-eye" />
              </a> -->

              <template v-if="row.actions.send_email_to_rep">
                <custom-button
                  title="Send Email"
                  iconClass="fas fa-envelope"
                  @click.native="openSendEmailModal(row)"
                />
              </template>
              <custom-button
                title="Send Upload File Link"
                iconClass="fas fa-link"
                @click.native="openSendFileUploadModal(row)"
              />

              <custom-button
                title="Send Reg/Login Email"
                iconClass="fas fa-lock"
                v-if="row.actions.send_rep_login_info"
                @onClicked="openModal(row, 'downlineSendLoginEmail')"
              />

              <!-- <br /> -->

              <!-- <custom-button
                title="Download Excel Report"
                iconClass="fas fa-file-excel"
              /> -->

              <template v-if="row.actions.send_rep_login_info">
                <custom-button
                  title="Send Information"
                  iconClass="far fa-paper-plane"
                  @click.native="openSendInfoModal(row)"
                />
              </template>
              <template v-if="rows.is_assign_contract && !isEnrollPrime ">
                  <custom-button
                    title="Send Contracts"
                    iconClass="far fa-file"
                    @click.native="openSendContractModal(row)"
                  />
              </template>
              <!-- Contract Download -->
              <template v-if="row.show_contract">
                <custom-button
                  title="View Contract Pdf"
                  iconClass="far fa-eye"
                  @click.native="openViewContractPdfModal(row)"
                />
              </template>

              <custom-route-button
                title="Assign Plan"
                iconClass="fas fa-plus"
                :routeLink="{
                  name: 'DownlineRepPlans',
                  params: { downlineRepId: encodedId(row.agent_id) },
                }"
              />

              <!-- <custom-button
                title="Send Rep Contracts"
                iconClass="fas fa-file-signature"
                v-b-modal.sendRepContract
              /> -->

              <custom-button
                v-if="!isEnrollPrime"
                title="Benefit Store"
                iconClass="fas fa-shopping-cart"
                @click.native="agentBenefitStoreLink(row.agent_id)"
              />
              <custom-route-button
                v-if="row.downline_details_count"
                title="View Rep Tree"
                iconClass="fas fa-sitemap"
                :routeLink="{
                  name: 'DownlineTree',
                  params: { agent_id: encodedId(row.agent_id) },
                }"
              />
              <custom-button
                v-if="row.actions.show_default_otp"
                title="View Default OTP"
                iconClass="fas fa-list"
                @onClicked="openModal(row, 'defaultOtpModal')"
              />
              <custom-route-button
                v-if="!isEnrollPrime"
                title="View License"
                iconClass="fas fa-id-card"
                :routeLink="{
                  name: 'Licenses',
                  params: { downlineRepId: encodedId(row.agent_id) },
                }"
              />
              <custom-button 
               v-if="!isEnrollPrime"
                title="Add License"
                iconClass="fas fa-plus-circle"
                @click.native="openAddLicenseModal(row)"
              />
              <a
                title="View msg"
                class="custom-btn"
                v-b-tooltip.hover="ViewMsg"
                @click="routeToViewMessage(row)"
              >
                <img src="../../../assets/images/view-msg.svg" />
              </a>
              <a
                title="Delete Agent"
                class="custom-btn"
                v-b-tooltip.hover="'Soft Delete Agent'"
                v-if="row.actions.is_deletable"
                @click="checkUpLineAgent(row.agent_id)"
              >
                <img
                  src="../../../assets/images/downline-delete.svg"
                  class="delete-agent-btn"
                />
              </a>
              <custom-button
                v-if="!isEnrollPrime"
                title="Restrict Platforms"
                iconClass="fa fa-server"
                @onClicked="openModal(row, 'restrict-platform-modal')"
              />

              <custom-button v-if="isVipUser && row.agent_status =='Approved'"
              title="Switch Account"
              iconClass="fas fa-exchange-alt"
              @onClicked="switchRep(row)"
            />
              <a
                href="javascript:void(0);"
                v-b-tooltip.hover
                title="Add Notes"
                @click="openAddNotesModal(row)"
              >
                <img src="@/assets/images/add-notes-rounded.svg" width="16px" alt="Add Agent Notes" />
              </a>
              <a
                href="javascript:void(0);"
                v-b-tooltip.hover
                title="View Notes"
                @click="openViewNotesModal(row)"
              >
                <img src="@/assets/images/notes-bold.svg" width="16px" alt="View Agent Notes" />
              </a>
            </div>
          </td>
          <td
            v-if="
              row.agent_status.toLowerCase() == 'deleted' &&
                row.actions.is_activatable
            "
          >
            <a
              title="reactive Agent"
              class="custom-btn"
              v-b-tooltip.hover="'Restore Agent'"
              @click="restoreAgent(row.agent_id)"
            >
              <img
                src="../../../assets/images/reactivate.svg"
                class="delete-agent-btn"
              />
            </a>
          </td>
        </tr>
      </tbody>
    </table>

    <downline-rep-send-email
      :agent="selectedAgent"
      @onClosedModal="onAgentModalClosed"
    />
    <downline-rep-send-info
      :agent="selectedAgent"
      @onClosedModal="onAgentModalClosed"
    />
    <send-representative-contract-modal
      :agent="selectedAgent"
      @onClosedModal="sendContract"
    />
    <view-contract-pdf-modal
      :agent="selectedAgent"
      @onClosedModal="viewContractPdfModal"
    />
    <assign-level-modal :agent="selectedAgent" @onClosedModal="assignLevel" />

    <downline-rep-send-login-email
      :agent="selectedAgent"
      @onClosedModal="onAgentModalClosed"
    />
    <default-otp-modal
      :row.sync="selectedAgent"
      rowType="A"
      @onCloseModal="selectedAgent = null"
    />
    <downline-list-modal
      :agentDownlineInfo="agentDownlineInfo"
      @getUserList="getUserList"
    />
    <signed-contract-modal :signedContractData="signedContract" />
    <restrict-platform-modal :row="selectedAgent" />
    <add-notes-modal
      :row="agentNote"
      :isOpen="showAddNotesModal"
      @close="showAddNotesModal = false"
    />
    <view-notes-modal
      :row="agentNote"
      :isOpen="showViewNotesModal"
      @close="showViewNotesModal = false"
    />
    <add-license-modal
      :agent="selectedAgent"
      @onSuccess="onAgentModalClosed"
    />
    <downline-file-upload-access userType="REP" />
  </div>
</template>
<script>
import Api from "@/resource/Api";
import DownlineRepSendInfo from "../partials/DownlineRepSendInfo";
import DownlineRepSendEmail from "../partials/DownlineRepSendEmail";
import DownlineRepSendLoginEmail from "../partials/DownlineRepSendLoginEmail";
import DefaultOtpModal from "@/components/ui/DefaultOtpModal.vue";
import CustomButton from "../../../components/ui/CustomButton.vue";
import SendRepresentativeContractModal from "./SendRepresentativeContractModal.vue";
import ViewContractPdfModal from "./ViewContractPdfModal.vue";
import AssignLevelModal from "./AssignLevelModal.vue";
import AdminApi from "@/resource/AdminApi";
import DownlineListModal from "./DownlineListModal";
import RestrictPlatformModal from "../modals/RestrictPlatformModal";
import SignedContractModal from "./SignedContractModal.vue";
import { mapGetters } from "vuex";
import isEnrollPrimeUtil from '@/utils/isEnrollPrimeUtil';
import AddNotesModal from '../modals/AddNotesModal.vue';
import ViewNotesModal from '../modals/ViewNotesModal.vue';
import AddLicenseModal from './AddLicenseModal.vue';
import DownlineFileUploadAccess from './DownlineFileUploadAccess.vue';


export default {
  name: "DownlineRepListTable",
  props: {
    rows: {
      required: true,
    },
    isInitialLoad: {
      type: Boolean,
      default: true,
    },
    activeQuickFilter: {
      type: String,
      default: null,
    },
  },
  computed: {
    ...mapGetters({ isVipUser: "vipUser" }),
    isEnrollPrime() {
      return isEnrollPrimeUtil.isEnrollPrime();
    }
  },
  components: {
    DownlineRepSendInfo,
    DownlineRepSendEmail,
    DownlineRepSendLoginEmail,
    DefaultOtpModal,
    CustomButton,
    SendRepresentativeContractModal,
    AssignLevelModal,
    DownlineListModal,
    ViewContractPdfModal,
    RestrictPlatformModal,
    SignedContractModal,
    AddNotesModal,
    ViewNotesModal,
    AddLicenseModal,
    DownlineFileUploadAccess,
  },
  data() {
    return {
      selectedAgent: null,
      downlinesDialog: false,
      agentDownlineInfo: {},
      signedContract: [],
      agentNote: null,
      showViewNotesModal: false,
    };
  },
  methods: {
    openModal(row, modalId) {
      this.selectedAgent = row;
      this.$bvModal.show(modalId);
    },
      getBusinessName(business) {business
    if (!business) return null;

    // If array (PROD)
    if (Array.isArray(business)) {
      return business.length ? business[0].business_name : null;
    }

    // If object (QA)
    return business.business_name || null;
  },
    encodedId(id) {
      return btoa(id);
    },
    openSendInfoModal(row) {
      this.selectedAgent = row;
      this.$bvModal.show("sendInfo");
    },
    openSendFileUploadModal(row) {
      this.selectedAgent = row;
      this.$bvModal.show("sendFileUploadAccess");
    },
    openSendContractModal(row) {
      this.selectedAgent = row;
      this.$bvModal.show("sendContract");
    },
    // new send representaive contract
    openNewSendContractModal(row) {
      this.selectedAgent = row;
      this.$bvModal.show("sendNewContract");
    },
    openSignedContractModal(row) {
      this.signedContract = row;
      this.$bvModal.show("signedContract");
    },
    openAssignLevelModal(row) {
      this.selectedAgent = row;
      this.$bvModal.show("assignLevel");
    },
    openViewContractPdfModal(row) {
      this.selectedAgent = row;
      this.$bvModal.show("viewContractPdfModal");
    },

    agentBenefitStoreLink(agentId) {
      const benefitStoreUrl = this.$endpoint.BENEFIT_STORE_URL;
      let url = `${benefitStoreUrl}/agent-home?agent_id=${btoa(agentId)}`;
      window.open(url, "_blank");
    },
    routeToViewMessage(row) {
      let app = this;
      let userInfo = {
        user_id: "",
        user_message_type: "",
      };
      userInfo.user_id = row.agent_id;
      userInfo.user_message_type = "A";
      app.$store.commit("setViewMessageUserInfo", userInfo);
      const routePath = app.$router.resolve("/view-msg").href;
      window.open(routePath, "_blank");
    },
    onAgentModalClosed() {
      this.selectedAgent = "";
    },
    openSendEmailModal(row) {
      this.selectedAgent = row;
      this.$bvModal.show("sendEmail");
    },
    enableDisableAgent(agentId, status) {
      const url = `v2/approve-downline-reps/${agentId}?enable=${status}}`;
      const [title, agentStatus] =
        status == 1
          ? ["Activate Agent", "activate"]
          : ["Disable Agent", "disable"];
      this.$confirm({
        title: title,
        message: `Do you want to ${agentStatus} this agent?`,
        button: {
          no: "No",
          yes: "Yes",
        },
        callback: (confirm) => {
          if (confirm) {
            let loader = this.$loading.show();
            Api.put(url)
              .then((res) => {
                let response = res.data.message;
                this.$notify(response);
                this.$emit("onSuccess");
              })
              .catch((err) => {
                this.$notify(err.response.data.message, "error");
              })
              .finally(() => {
                loader.hide();
              });
          }
        },
      });
    },
    enableEliteBlue(agentId) {
      const url = `v2/enable-disable-rep-elite-blue/${agentId}?enable=1`;
      this.$confirm({
        title: "Enable Elite Blue",
        message: `Do you want to enable elite blue for this agent?`,
        button: {
          no: "No",
          yes: "Yes",
        },
        callback: (confirm) => {
          if (confirm) {
            let loader = this.$loading.show();
            Api.put(url)
              .then((res) => {
                let response = res.data.message;
                this.$notify(response);
                this.$emit("onSuccess");
              })
              .catch((err) => {
                this.$notify(err.response.data.message, "Error", "danger");
              })
              .finally(() => {
                loader.hide();
              });
          }
        },
      });
    },
    restoreAgent(agentId) {
      this.$confirm({
        title: "Confirm",
        message: "Do you want to restore this agent ?",
        button: {
          no: "No",
          yes: "Yes",
        },
        callback: (confirm) => {
          let loader = this.$loading.show();
          const url = `/api/v2/restore-agent/${agentId}`;
          if (confirm) {
            AdminApi.get(url)
              .then((res) => {
                let message = res.data.message;
                this.$notify(message, "success");
                this.$emit("onSuccess");
              })
              .catch((err) => {
                this.$notify(err.response.data.message, "error");
              })
              .finally(() => {
                loader.hide();
              });
          } else {
            loader.hide();
          }
        },
      });
    },
    checkUpLineAgent(agentId) {
      let url = `/api/v2/active-downlines-list/${agentId}`;
      let loader = this.$loading.show();
      AdminApi.get(url)
        .then((res) => {
          let data = res.data.data;
          if (data.active_agents.length == 0 && !data.active_members) {
            this.deleteAgent(agentId);
          } else {
            this.agentDownlineInfo = data;
            this.agentDownlineInfo["agentId"] = agentId;
            loader.hide();
            this.$bvModal.show("showDownlineList");
          }
        })
        .catch((err) => {
          this.$notify(err.response.message, "error");
          console.log(err);
        })
        .finally(() => {
          loader.hide();
        });
    },
    deleteAgent(agentId) {
      this.$confirm({
        title: "Confirm",
        message: "Do you want to delete this agent?",
        button: {
          no: "No",
          yes: "Yes",
        },
        callback: (confirm) => {
          const url = "/api/v2/delete-agent";
          let data = { agent_id: agentId };
          let loader = this.$loading.show();
          if (confirm) {
            AdminApi.delete(url, data)
              .then((res) => {
                let message = res.data.message;
                this.$notify(message, "success");
                this.$emit("onSuccess");
              })
              .catch((err) => {
                console.log(err);
                this.$notify("Failed to delete agent", "error");
              })
              .finally(() => {
                loader.hide();
              });
          } else {
            loader.hide();
          }
        },
      });
    },
    getUserList() {
      this.$emit("onSuccess");
      this.$bvModal.hide("showDownlineList");
    },
    getStatTotal(activeTotal, withdrawTotal, termedTotal) {
      let total = 0;
      total =
        parseInt(activeTotal) + parseInt(withdrawTotal) + parseInt(termedTotal);
      return total;
    },
    async switchRep(row) {
      const { switchAccount } = await import('@/utils/switchAccount');
      await switchAccount.call(this, row.agent_email);
    },
    openAddNotesModal(row) {
      this.agentNote=row;
      this.showAddNotesModal = true;
      this.$bvModal.show("addAgentNotesModal");
    },
    openViewNotesModal(row) {
      this.agentNote=row;
      this.showViewNotesModal = true;
      this.$bvModal.show("viewAgentNotesModal"); 
    },
    openAddLicenseModal(row) {
      this.selectedAgent = row;
      this.$bvModal.show("addLicenseModal");
    },
  },
};
</script>
<style>
.valid-icon {
  width: 4%;
}

.viewClient-btn {
  display: inline-block;
  text-align: center;
  color: #fff;
  background: #02bc77;
  padding: 8px 25px;
  border-radius: 6px;
  margin-top: 8px;
}

 .downline-rep-list-table .custom-btn {
  top: 0% !important;
}

.delete-agent-btn {
  width: 25px;
  position: relative;
  top: -4px;
  height: 25px;
}

.modal-header {
  display: block;
  text-align: center;
}
.signed-contract-details {
  color: #1890ff;
  text-decoration: none;
  background-color: transparent;
  outline: none;
  cursor: pointer;
  transition: color 0.3s;
  -webkit-text-decoration-skip: objects;
}
/* Company Name styling - keeps label together */
.company-name strong {
  display: inline-block;
  white-space: nowrap;
}

/* Remove horizontal scrollbar */
.downline-rep-list-table {
  overflow-x: visible;
}

.downline-rep-list-table table {
  table-layout: auto;
  width: 100%;
}

.downline-rep-list-table table tr td {
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.empty-state-cell {
  padding: 2.5rem 1rem;
  text-align: center;
}

.empty-state-msg {
  color: #3BC6F2;
  font-size: 14px;
  font-style: italic;
  font-weight: bold;
  display: inline-block;
}

.quick-date-btns-inline {
  gap: 8px;
}

.quick-date-btn-inline {
  font-size: 13px;
  font-weight: 600;
  padding: 6px 16px;
  border-radius: 6px;
  border: 1.5px solid #3BC6F2;
  background-color: #e8f7fd;
  color: #3BC6F2;
  cursor: pointer;
  transition: all 0.18s ease;
  white-space: nowrap;
  height: 36px;
  line-height: 1;
}

.quick-date-btn-inline:hover {
  background-color: #3BC6F2;
  border-color: #3BC6F2;
  color: #fff;
}

.quick-date-btn-inline.active {
  background-color: #3BC6F2;
  border-color: #3BC6F2;
  color: #fff;
  box-shadow: 0 2px 8px rgba(59, 198, 242, 0.32);
}

@media (max-width: 768px) {
  .quick-date-btn-inline {
    font-size: 12px;
    padding: 5px 10px;
  }
}
</style>
